/**
 * Datas de calendário são strings "AAAA-MM-DD" (sem fuso).
 * Funções que dependem de "hoje" recebem a data como parâmetro, para serem
 * determinísticas e testáveis; quem chama decide o fuso.
 */
export type ISODate = string;

const pad = (n: number) => String(n).padStart(2, '0');

export const toISODate = (d: Date): ISODate => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export function parseISODate(s: ISODate): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function addDays(s: ISODate, n: number): ISODate {
  const d = parseISODate(s);
  d.setDate(d.getDate() + n);
  return toISODate(d);
}

/** Dias inteiros de `from` até `to` (positivo se `to` é depois). */
export const daysBetween = (from: ISODate, to: ISODate) =>
  Math.round((parseISODate(to).getTime() - parseISODate(from).getTime()) / 864e5);

/** Domingo da semana que contém `s`. */
export function startOfWeek(s: ISODate): ISODate {
  const d = parseISODate(s);
  d.setDate(d.getDate() - d.getDay());
  return toISODate(d);
}

export const weekday = (s: ISODate) => parseISODate(s).getDay();

/** "Hoje" num fuso IANA, independente do fuso do processo. */
export function todayIn(timeZone: string, now = new Date()): ISODate {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const get = (t: string) => parts.find(p => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export const localToday = (): ISODate => toISODate(new Date());

/** Dias completos desde um instante ISO (nunca negativo). */
export const daysSince = (iso: string | null | undefined, now = Date.now()) =>
  iso ? Math.max(0, Math.floor((now - new Date(iso).getTime()) / 864e5)) : 0;

/** "HH:MM" → minutos desde 00:00. */
export function toMinutes(hhmm: string | null | undefined): number {
  const [a, b] = String(hhmm || '0:0').split(':').map(Number);
  return a * 60 + b;
}

export const fromMinutes = (m: number) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;

/** Próximo dia útil a partir de `s` (pula sábado e domingo). */
export function nextBusinessDay(s: ISODate): ISODate {
  const w = weekday(s);
  if (w === 6) return addDays(s, 2);
  if (w === 0) return addDays(s, 1);
  return s;
}
